// Design System Dialog Component
// Unified modal/dialog component

import React, { forwardRef } from 'react';
import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Typography,
  Box,
  styled,
  Slide,
  Fade,
  Zoom,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens, shadowTokens } from '../../tokens';

// Transition components
const Transition = {
  slide: forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />),
  fade: forwardRef((props, ref) => <Fade ref={ref} {...props} />),
  zoom: forwardRef((props, ref) => <Zoom ref={ref} {...props} />),
};

// Styled components
const StyledDialog = styled(MuiDialog)(({ theme, fullScreen }) => ({
  '& .MuiDialog-paper': {
    borderRadius: fullScreen ? 0 : borderTokens.lg,
    boxShadow: shadowTokens.dialog,
    backgroundImage: 'none',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme, divider }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: spacingTokens.layout.lg,
  ...(divider && {
    borderBottom: `1px solid ${theme.palette.divider}`,
  }),
}));

const StyledDialogContent = styled(DialogContent)(({ theme, divider }) => ({
  padding: spacingTokens.layout.lg,
  ...(divider && {
    paddingTop: spacingTokens.layout.lg,
  }),
}));

const StyledDialogActions = styled(DialogActions)(({ theme, divider }) => ({
  padding: spacingTokens.layout.lg,
  ...(divider && {
    borderTop: `1px solid ${theme.palette.divider}`,
  }),
}));

/**
 * Dialog - Unified modal/dialog component
 * 
 * Features:
 * - Multiple sizes (sm, md, lg, xl, fullWidth, fullScreen)
 * - Transition animations
 * - Close button option
 * - Dividers between sections
 * - Confirmation dialog preset
 * - Scrollable content
 * 
 * @example
 * // Basic dialog
 * <Dialog open={open} onClose={handleClose} title="Dialog Title">
 *   <DialogContent>
 *     Dialog content here
 *   </DialogContent>
 *   <DialogActions>
 *     <Button onClick={handleClose}>Cancel</Button>
 *     <Button variant="contained" onClick={handleSave}>Save</Button>
 *   </DialogActions>
 * </Dialog>
 * 
 * // Confirmation dialog
 * <Dialog
 *   open={open}
 *   onClose={handleClose}
 *   title="Confirm Action"
 *   content="Are you sure you want to delete this item?"
 *   actions={[
 *     { label: 'Cancel', onClick: handleClose },
 *     { label: 'Delete', onClick: handleDelete, color: 'error', variant: 'contained' }
 *   ]}
 * />
 */
const Dialog = forwardRef(({
  open,
  onClose,
  
  // Header
  title,
  subtitle,
  showCloseButton = true,
  headerAction,
  
  // Content
  children,
  content, // For simple text content
  
  // Actions
  actions, // Array of action configs
  
  // Styling
  size = 'md',
  fullScreen = false,
  fullWidth = false,
  dividers = false,
  scroll = 'paper',
  
  // Behavior
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  transition = 'fade',
  transitionDuration = 225,
  
  // Component props
  titleProps = {},
  contentProps = {},
  actionsProps = {},
  
  ...props
}, ref) => {
  // Handle backdrop click
  const handleBackdropClick = (event, reason) => {
    if (reason === 'backdropClick' && disableBackdropClick) {
      return;
    }
    onClose?.(event, reason);
  };
  
  // Handle escape key
  const handleEscapeKeyDown = (event) => {
    if (disableEscapeKeyDown) {
      return;
    }
    onClose?.(event, 'escapeKeyDown');
  };
  
  // Size mapping
  const maxWidth = {
    xs: 'xs',
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl',
  }[size] || size;
  
  return (
    <StyledDialog
      ref={ref}
      open={open}
      onClose={handleBackdropClick}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      scroll={scroll}
      TransitionComponent={Transition[transition] || Transition.fade}
      transitionDuration={transitionDuration}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          handleEscapeKeyDown(e);
        }
      }}
      {...props}
    >
      {/* Title */}
      {(title || showCloseButton) && (
        <StyledDialogTitle divider={dividers} {...titleProps}>
          <Box>
            {title && (
              <Typography variant="h6" component="div">
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {headerAction}
            {showCloseButton && (
              <IconButton
                aria-label="close"
                onClick={(e) => onClose?.(e, 'closeButton')}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </StyledDialogTitle>
      )}
      
      {/* Content */}
      {(children || content) && (
        <StyledDialogContent divider={dividers} {...contentProps}>
          {content && !children && (
            <DialogContentText>{content}</DialogContentText>
          )}
          {children}
        </StyledDialogContent>
      )}
      
      {/* Actions */}
      {actions && actions.length > 0 && (
        <StyledDialogActions divider={dividers} {...actionsProps}>
          {actions.map((action, index) => {
            const { label, onClick, component: Component = 'button', ...actionProps } = action;
            
            if (Component === 'button') {
              const Button = require('../core/Button').default;
              return (
                <Button key={index} onClick={onClick} {...actionProps}>
                  {label}
                </Button>
              );
            }
            
            return <Component key={index} onClick={onClick} {...actionProps}>{label}</Component>;
          })}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
});

Dialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  
  // Header
  title: PropTypes.node,
  subtitle: PropTypes.node,
  showCloseButton: PropTypes.bool,
  headerAction: PropTypes.node,
  
  // Content
  children: PropTypes.node,
  content: PropTypes.string,
  
  // Actions
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    component: PropTypes.elementType,
    // Any other props for the action component
  })),
  
  // Styling
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'fullWidth']),
  fullScreen: PropTypes.bool,
  fullWidth: PropTypes.bool,
  dividers: PropTypes.bool,
  scroll: PropTypes.oneOf(['paper', 'body']),
  
  // Behavior
  disableBackdropClick: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
  transition: PropTypes.oneOf(['slide', 'fade', 'zoom']),
  transitionDuration: PropTypes.number,
  
  // Component props
  titleProps: PropTypes.object,
  contentProps: PropTypes.object,
  actionsProps: PropTypes.object,
};

Dialog.displayName = 'Dialog';

// Export subcomponents
export { DialogTitle, DialogContent, DialogActions, DialogContentText };
export default Dialog;